/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useMemo, useState } from "react";
import { YEARLY_COMPILED_REPORT_URL } from "../../../../api/api_routing_urls";
import useAxiosPrivate from "../../../../authentication/hooks/useAxiosPrivate";
import Dashboard from "../../../../common/Dashboard";
import useAuth from "../../../../authentication/hooks/useAuth";
import H2withIcon from "../../../../reusable-components/headings/H2withIcon";
import { MdAssignmentTurnedIn, MdInsertChartOutlined } from "react-icons/md";
import LoadingModal from "../../../../reusable-components/modals/LoadingModal";
import Button from "../../../../reusable-components/buttons/Button";
import FiscalYearDropDown from "../../../../reusable-components/search/FiscalYearDropDown";
import { formatINRCurrency } from "../../../../utilities/currency/currency";
import { PrintReport } from "../PrintReport/PrintReport";
import YearlyCompiledReportConfig_Print from "./YearlyCompiledReportConfig_Print";
import YearlyCompiledCard from "./YearlyCompiledCard";

const YearlyCompiledReportConfig = () => {
  const { auth } = useAuth();

  const axiosPrivate = useAxiosPrivate();
  const controller = new AbortController();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);

  const [activeBorrower, setActiveBorrower] = useState(0);
  const [totalDisbursed, setTotalDisbursed] = useState(0);
  const [paymentReceived, setPaymentReceived] = useState(0);
  const [totalDueAmount, setTotalDueAmount] = useState(0);
  const [totalPrincipleDue, setTotalPrincipleDue] = useState(0);
  const [totalPrincipleOutstanding, setTotalPrincipleOutstanding] = useState(0);
  const [totalInterestDue, setTotalInterestDue] = useState(0);

  const [ioiDue, setIoiDue] = useState(0);
  const [penalDue, setPenalDue] = useState(0);

  const [categoryWiseDetails, setCategoryWiseDetails] = useState([]);
  const [showPrint, setShowPrint] = useState(false);

  const currentYear = new Date().getFullYear();

  const fiscalYears = Array.from(
    { length: +currentYear - 1996 },
    (_, index) => 1996 + index
  );

  // Category Wise Dashboard Report

  const getYearlyCompiledReport = async () => {
    try {
      setIsLoading(true);
      const response = await axiosPrivate.get(
        YEARLY_COMPILED_REPORT_URL,
        {
          params: {
            end_date: selectedFiscalYear || `${currentYear}-04-01`,
          },
        },
        {
          signal: controller.signal,
        }
      );

      if (response?.status === 200) {
        setCategoryWiseDetails(response?.data?.report);
        getDashboardReport(response?.data?.report);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setShowPrint(true);
    }
  };

  const getDashboardReport = (details) => {
    let active_Borrower = 0;
    let total_Disbursed = 0;
    let total_Repay_Amount = 0;
    let total_Due = 0;
    let total_principle_Due = 0;
    let total_interest_Due = 0;
    let total_principle_outstanding = 0;

    let total_ioi_due = 0;
    let total_penal_due = 0;

    details.map((obj) => {
      active_Borrower += +obj.borrower;
      total_Disbursed += +obj.di_amount;
      total_Repay_Amount += +obj.rp_amount;
      total_Due += +obj.due_amount;
      total_principle_Due += +obj.principle_due;
      total_principle_outstanding += +obj.principle_outstanding;
      total_interest_Due += +obj.int_due_amount;

      total_ioi_due += +obj.ioi_due;
      total_penal_due += +obj.penal_due;
    });

    setActiveBorrower(active_Borrower);
    setTotalDisbursed(total_Disbursed);
    setPaymentReceived(total_Repay_Amount);
    setTotalDueAmount(total_Due);
    setTotalPrincipleDue(total_principle_Due);
    setTotalInterestDue(total_interest_Due);
    setTotalPrincipleOutstanding(total_principle_outstanding);

    setIoiDue(total_ioi_due);
    setPenalDue(total_penal_due);
  };

  useMemo(() => {
    setShowPrint(false);
    setTotalPrincipleDue(totalPrincipleDue);
    setTotalInterestDue(totalInterestDue);
  }, [selectedFiscalYear]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      getYearlyCompiledReport();
      setShowPrint(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <Dashboard sidebarType={auth?.rolename}>
        {isLoading ? (
          <>
            <LoadingModal message={"Loading"} />
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <H2withIcon>
                <MdAssignmentTurnedIn />{" "}
                <span className="pl-1.5">Yearly Compiled Report</span>
              </H2withIcon>
            </div>
            <>
              <section className="flex justify-between gap-3 items-end mb-2 ">
                <div>
                  <FiscalYearDropDown
                    defaultName="filterFiscalYear"
                    required={true}
                    classes="text-sm w-full"
                    placeholder="Select Fiscal Year..."
                    data={fiscalYears}
                    defaultValue={selectedFiscalYear}
                    setSelected={setSelectedFiscalYear}
                    selected={selectedFiscalYear}
                    maxMenuHeight={120}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    className={
                      "bg-primary text-white text-sm md:px-6 py-2 min-w-36 flex items-center justify-center gap-1 rounded-sm"
                    }
                    onClick={() => getYearlyCompiledReport()}
                  >
                    <span className="mr-1">Generate Report</span>
                    <MdInsertChartOutlined size={18} />
                  </Button>
                </div>
              </section>
            </>
            <section className="mt-9">
              <div className="border border-slate-300 p-6">
                <div className="text-lg md:text-xl text-primary font-semibold text-center border bg-slate-100 p-1">
                  All Category
                </div>
                <div className=" bg-slate-50  text-lg grid grid-cols-2 gap-x-3 md:gap-x-6 pt-6">
                  <div className="grid grid-cols-2 items-center">
                    Borrowers:{" "}
                    <span className="font-bold"> {activeBorrower || 0}</span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Disbursed Amount:
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(totalDisbursed)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Payment Received:
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(paymentReceived)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Due Amount:
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(totalDueAmount)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Principle Outstanding:
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(totalPrincipleOutstanding)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Principle Due:
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(totalPrincipleDue)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Interest Due Amount:
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(totalInterestDue)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Interest on Interest Due :
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(ioiDue)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 items-center">
                    Penal Interest Due :
                    <span className="font-bold">
                      {" "}
                      {formatINRCurrency(penalDue)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2  gap-3 md:gap-6 mt-6">
                {categoryWiseDetails?.length > 0
                  ? categoryWiseDetails?.map(
                      ({
                        borrower,
                        categoryname,
                        di_amount,
                        due_amount,
                        rp_amount,
                        principle_due,
                        principle_outstanding,
                        int_due_amount,
                        ioi_due,
                        penal_due,
                      }) => (
                        <>
                          {categoryname ? (
                            <>
                              <YearlyCompiledCard
                                borrower={borrower}
                                categoryname={categoryname}
                                di_amount={di_amount}
                                due_amount={due_amount}
                                rp_amount={rp_amount}
                                principle_due={principle_due}
                                principle_outstanding={principle_outstanding}
                                int_due_amount={int_due_amount}
                                ioi_due={ioi_due}
                                penal_due={penal_due}
                              />
                            </>
                          ) : null}
                        </>
                      )
                    )
                  : null}
              </div>
            </section>

            {showPrint ? (
              <div className="grid grid-cols-1 mt-6">
                {categoryWiseDetails?.length > 0 ? (
                  <PrintReport reportTitle={"Yearly Compiled Report"}>
                    <YearlyCompiledReportConfig_Print
                      selectedFiscalYear={selectedFiscalYear}
                      activeBorrower={activeBorrower}
                      totalDisbursed={totalDisbursed}
                      paymentReceived={paymentReceived}
                      totalDueAmount={totalDueAmount}
                      categoryWiseDetails={categoryWiseDetails}
                      totalInterestDue={totalInterestDue}
                      totalPrincipleDue={totalPrincipleDue}
                      totalPrincipleOutstanding={totalPrincipleOutstanding}
                      ioiDue={ioiDue}
                      penalDue={penalDue}
                    />
                  </PrintReport>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Dashboard>
    </>
  );
};

export default YearlyCompiledReportConfig;
